import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { Globe, Palette, Shield } from "lucide-react";

interface WhitelabelConfig {
  customDomain: string | null;
  logoUrl: string | null;
  primaryColor: string;
  appName: string | null;
  supportEmail: string | null;
  welcomeMessage: string | null;
  domainVerificationToken: string | null;
  domainVerified: boolean;
}

interface WhitelabelConfigResponse {
  data: {
    orgId: string;
    name: string;
    config: Partial<WhitelabelConfig>;
  };
}

interface DomainVerificationResponse {
  data: {
    domain: string;
    txtRecord: string;
    txtValue: string;
    instructions: string;
  };
}

const DEFAULT_CONFIG: WhitelabelConfig = {
  customDomain: null,
  logoUrl: null,
  primaryColor: "#2563eb",
  appName: null,
  supportEmail: null,
  welcomeMessage: null,
  domainVerificationToken: null,
  domainVerified: false,
};

export default function WhitelabelConfigPage() {
  const [orgId, setOrgId] = useState<string>("");
  const [config, setConfig] = useState<WhitelabelConfig | null>(null);
  const [orgName, setOrgName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verifyInfo, setVerifyInfo] = useState<DomainVerificationResponse["data"] | null>(null);

  const fetchConfig = useCallback(async (targetOrgId: string) => {
    setIsLoading(true);

    try {
      const res = await apiFetch<WhitelabelConfigResponse>(`/platform/agency/whitelabel/${targetOrgId}`);
      setOrgName(res.data.name);
      setConfig({
        ...DEFAULT_CONFIG,
        ...(res.data.config ?? {}),
      });
      setError(null);
    } catch {
      setConfig(null);
      setError("White-label settings are currently unavailable.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("orgId") ?? "";
    setOrgId(id);

    if (!id) {
      setConfig(null);
      setIsLoading(false);
      return;
    }

    void fetchConfig(id);
  }, [fetchConfig]);

  const handleSave = async () => {
    if (!orgId || !config) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await apiFetch(`/platform/agency/whitelabel/${orgId}`, {
        method: "PUT",
        body: JSON.stringify(config),
      });
      setSuccess("White-label settings saved.");
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError("Failed to save white-label settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!orgId || !config?.customDomain) {
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const res = await apiFetch<DomainVerificationResponse>(
        `/platform/agency/whitelabel/${orgId}/verify-domain`,
        { method: "POST" },
      );
      setVerifyInfo(res.data);
      setConfig({
        ...config,
        domainVerified: false,
      });
      setSuccess("Verification TXT record generated.");
    } catch {
      setError("Failed to generate domain verification instructions.");
    } finally {
      setIsVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-56" />
        {[1, 2, 3].map((i) => (
          <SkeletonCard key={i} className="h-24" />
        ))}
      </div>
    );
  }

  if (!orgId) {
    return (
      <EmptyState
        title="Organization required"
        description="Open this admin white-label page with an `orgId` query parameter so the mounted organization branding settings can be loaded."
      />
    );
  }

  if (!config) {
    return (
      <EmptyState
        title="White-label settings unavailable"
        description={error ?? "The mounted organization white-label settings could not be loaded."}
      />
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-[var(--color-primary)]" />
          <div>
            <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">White-Label Configuration</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Mounted branding controls for {orgName || "the selected agency"}.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm text-white hover:opacity-90 disabled:opacity-50"
        >
          {isSaving ? "Saving..." : "Save Branding"}
        </button>
      </div>

      {success ? (
        <div className="rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-4 py-2 text-sm text-[var(--color-success)]">
          {success}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-2 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}

      <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-[var(--color-primary)]" />
          <h2 className="font-medium text-[var(--color-foreground)]">Branding</h2>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1">
            <span className="block text-xs text-[var(--color-muted-foreground)]">App Name</span>
            <input
              value={config.appName ?? ""}
              onChange={(e) => setConfig({ ...config, appName: e.target.value || null })}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              placeholder="Your Agency Name"
            />
          </label>

          <label className="space-y-1">
            <span className="block text-xs text-[var(--color-muted-foreground)]">Support Email</span>
            <input
              type="email"
              value={config.supportEmail ?? ""}
              onChange={(e) => setConfig({ ...config, supportEmail: e.target.value || null })}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              placeholder="support@youragency.com"
            />
          </label>

          <label className="space-y-1 md:col-span-2">
            <span className="block text-xs text-[var(--color-muted-foreground)]">Logo URL</span>
            <input
              value={config.logoUrl ?? ""}
              onChange={(e) => setConfig({ ...config, logoUrl: e.target.value || null })}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              placeholder="https://cdn.example.com/logo.png"
            />
          </label>

          <label className="space-y-1 md:col-span-2">
            <span className="block text-xs text-[var(--color-muted-foreground)]">Welcome Message</span>
            <textarea
              value={config.welcomeMessage ?? ""}
              onChange={(e) => setConfig({ ...config, welcomeMessage: e.target.value || null })}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-3 text-sm text-[var(--color-foreground)]"
              rows={3}
            />
          </label>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-[var(--color-foreground)]">Primary Color</span>
          <input
            type="color"
            value={config.primaryColor}
            onChange={(e) => setConfig({ ...config, primaryColor: e.target.value })}
            className="h-8 w-8 rounded border border-[var(--color-border)] cursor-pointer"
          />
          <span className="text-xs font-mono text-[var(--color-muted-foreground)]">{config.primaryColor}</span>
        </div>
      </div>

      <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[var(--color-primary)]" />
          <h2 className="font-medium text-[var(--color-foreground)]">Custom Domain</h2>
        </div>

        <label className="space-y-1">
          <span className="block text-xs text-[var(--color-muted-foreground)]">App Domain</span>
          <input
            value={config.customDomain ?? ""}
            onChange={(e) => {
              setVerifyInfo(null);
              setConfig({ ...config, customDomain: e.target.value || null });
            }}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            placeholder="app.youragency.com"
          />
        </label>

        <div className="flex items-center gap-3">
          <span className={`text-xs ${config.domainVerified ? "text-[var(--color-success)]" : "text-[var(--color-muted-foreground)]"}`}>
            {config.domainVerified ? "Domain verified" : "Domain not verified"}
          </span>
          {config.customDomain ? (
            <button
              type="button"
              onClick={handleVerifyDomain}
              disabled={isVerifying}
              className="rounded border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-primary)] hover:bg-[var(--color-primary)]/5 disabled:opacity-50"
            >
              {isVerifying ? "Generating TXT record..." : "Verify Domain"}
            </button>
          ) : null}
        </div>

        {verifyInfo ? (
          <div className="rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-3 text-sm">
            <p className="font-medium text-[var(--color-primary)]">DNS verification instructions</p>
            <p className="mt-2 text-[var(--color-foreground)]">{verifyInfo.instructions}</p>
            <div className="mt-3 rounded-md bg-[var(--color-background)] p-3 font-mono text-xs text-[var(--color-foreground)]">
              <p>Name: {verifyInfo.txtRecord}</p>
              <p>Value: {verifyInfo.txtValue}</p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
